import { message } from "antd";
import PIOPIY from "piopiyjs";
import { platformApi } from "../api";

let piopiy = null;

const getPiopiy = () => piopiy;

const initializePiopiy = () => {
  if (piopiy) return piopiy;

  const newInstance = new PIOPIY({
    name: "Display Name",
    debug: false,
    autoplay: true,
    ringTime: 60,
  });

  piopiy = newInstance;
  return newInstance;
};

const piopiyLogin = async (obj, username, isReconnect = false) => {
  try {
    // Don't reload on reconnect attempts
    if (piopiy && !isReconnect) {
      return piopiy;
    }

    const instance = initializePiopiy();

    // CRITICAL FIX: Always update the global piopiy reference
    piopiy = instance;

    await instance.login(username, "Chand@1104l", "sbcind.telecmi.com");

    localStorage.setItem(
      "piopiyLoginState",
      JSON.stringify({
        username,
        timestamp: Date.now(),
      })
    );

    if (!isReconnect) {
      message.success("Piopiy login successful");
    }

    // CRITICAL FIX: Return the logged-in instance
    return instance;
  } catch (error) {
    console.error("Piopiy login error:", error);
    if (!isReconnect) {
      message.error("Failed to login to Piopiy. Please try again.");
    }

    // CRITICAL FIX: Clear both references on error
    piopiy = null;
    localStorage.removeItem("piopiyLoginState");

    if (!isReconnect) {
      window.location.reload();
    }

    throw error; // Re-throw to handle in calling code
  }
};

const piopiyDialUser = (
  obj,
  selectedNumber,
  UniqueId,
  module = "Follow-Ups",
  moduleid = null
) => {
  try {
    const token = localStorage.getItem("token");
    
    if (!token) {
      const errorMsg = "Token expired. Can't place call, Login again";
      message.error(errorMsg);
      throw new Error(errorMsg);
    }

    // Prefer live singleton — imported `piopiy` binding can be stale
    const instance = getPiopiy() || obj;
    if (!instance || typeof instance.call !== "function") {
      const errorMsg = "Piopiy instance not available. Please login first.";
      message.error(errorMsg);
      throw new Error(errorMsg);
    }

    const data = JSON.stringify({
      module,
      moduleid,
      uniqueId: UniqueId,
      token: token,
    });

    instance.call(selectedNumber, { extra_param: data });
  } catch (error) {
    console.error("Dial error:", error);
    message.error(error.message || "Failed to place call");
    throw error;
  }
};

const answerincommingCall = (obj) => {
  if (!obj || typeof obj.answer !== "function") {
    console.error("Invalid piopiy instance for answering call");
    return;
  }
  obj.answer({ custom: '{"name":"sano"}' }); // Fixed JSON syntax
};

const declineIncommingCall = (obj) => {
  if (!obj || typeof obj.reject !== "function") {
    console.error("Invalid piopiy instance for rejecting call");
    return;
  }
  obj.reject();
};

const declineOutgoingCall = (obj) => {
  try {
    if (!obj || typeof obj.terminate !== "function") {
      console.error("Invalid piopiy instance for terminating call");
      return;
    }
    obj.terminate();
  } catch (error) {
    console.error("Terminate call error:", error);
  }
};

/** Normalize to TeleCMI dial format: 91 + 10-digit mobile */
const normalizeTelecmiNumber = (raw) => {
  if (!raw) return null;
  const digits = String(raw).replace(/\D/g, "");
  if (digits.length === 10) return `91${digits}`;
  if (digits.length === 12 && digits.startsWith("91")) return digits;
  if (digits.length === 11 && digits.startsWith("0")) {
    return `91${digits.slice(1)}`;
  }
  return null;
};

/**
 * Strip to Indian 10-digit mobile for DB lookup / customer form.
 * Handles +91…, 91…, 0…, and plain 10-digit — does NOT use fixed slice(2).
 */
const toIndianMobile10 = (raw) => {
  if (raw == null || raw === "") return null;
  const digits = String(raw).replace(/\D/g, "");
  if (digits.length < 10) return null;
  return digits.slice(-10);
};

const isDialableMobile = (raw) => Boolean(normalizeTelecmiNumber(raw));

/**
 * Start TeleCMI warm transfer to an employee mobile (PSTN).
 * Employee rings first; customer is connected only after mergeCall().
 * @param {string} toNumber - 10-digit or 91xxxxxxxxxx
 */
const transferCall = (toNumber) => {
  const instance = getPiopiy();
  if (!instance || typeof instance.transfer !== "function") {
    const errorMsg = "Piopiy instance not available. Please login first.";
    message.error(errorMsg);
    throw new Error(errorMsg);
  }

  const callId =
    typeof instance.getCallId === "function" ? instance.getCallId() : null;
  if (!callId) {
    const errorMsg = "No active call to redirect. Stay on the live call and try again.";
    message.error(errorMsg);
    throw new Error(errorMsg);
  }

  const normalized = normalizeTelecmiNumber(toNumber);
  if (!normalized) {
    const errorMsg = "Invalid mobile number for redirect";
    message.error(errorMsg);
    throw new Error(errorMsg);
  }

  console.log("[TeleCMI] transfer start", { callId, to: normalized });
  instance.transfer(normalized);
  return normalized;
};

/**
 * Bridge customer + transferred party on INBOUND calls (TeleCMI DTMF 0).
 * Do NOT use this to complete OUTBOUND redirects — DTMF 0 drops the employee leg.
 */
const mergeCall = () => {
  const instance = getPiopiy();
  if (!instance || typeof instance.merge !== "function") {
    throw new Error("Piopiy merge not available");
  }
  console.log("[TeleCMI] merge (inbound connect customer to employee)");
  instance.merge();
};

/**
 * Complete OUTBOUND attended redirect: agent hangs up so TeleCMI bridges
 * customer ↔ employee. Classic attended-transfer complete.
 */
const completeOutboundTransfer = () => {
  const instance = getPiopiy();
  if (!instance || typeof instance.terminate !== "function") {
    throw new Error("Piopiy terminate not available");
  }
  console.log(
    "[TeleCMI] outbound redirect complete — agent hangup to bridge customer+employee"
  );
  instance.terminate();
};

/** Cancel in-progress transfer (TeleCMI sends DTMF #). */
const cancelTransfer = () => {
  const instance = getPiopiy();
  if (!instance || typeof instance.cancel !== "function") {
    return;
  }
  console.log("[TeleCMI] cancel transfer");
  instance.cancel();
};

const clearPiopiyState = () => {
  piopiy = null;
  localStorage.removeItem("piopiyLoginState");
};

const getTelecmiUsers = () => {
  // Read at call time — module-load cache was stale and skipped offline toggle
  const selectedUserId = localStorage.getItem("selectedTelecmiUserId");
  if (!selectedUserId) return Promise.resolve();

  return platformApi.get("/api/telecmi/teleuser").then((res) => {
    const users = res.data.data || [];
    const findCurr = users.find((obj) => obj.id === selectedUserId);
    if (findCurr?.status === true) {
      return platformApi.post(
        `/api/telecmi/teleUser/toggleStatus/${selectedUserId}`
      );
    }
  });
};

const logoutPiopiy = async () => {
  try {
    if (piopiy) {
      message.info("Piopiy Account Logging out");
      await getTelecmiUsers();

      if (typeof piopiy?.removeAllListeners === "function") {
        piopiy.removeAllListeners();
      }

      // CRITICAL FIX: Clear state before logout to prevent race conditions
      const instanceToLogout = piopiy;
      clearPiopiyState();

      await instanceToLogout.logout();

      setTimeout(() => {
        window.location.reload();
        message.info("Piopiy Account Logged out");
      }, 1000);
    }
  } catch (error) {
    console.error("Logout error:", error);
    clearPiopiyState();
    window.location.reload();
  }
};

export {
  piopiy,
  getPiopiy,
  piopiyLogin,
  piopiyDialUser,
  answerincommingCall,
  declineIncommingCall,
  declineOutgoingCall,
  normalizeTelecmiNumber,
  toIndianMobile10,
  isDialableMobile,
  transferCall,
  mergeCall,
  completeOutboundTransfer,
  cancelTransfer,
  logoutPiopiy,
};
